/**
 * Acquisition channels — organic, paid, referral.
 *
 * Paid budget modes:
 * - `link` (default): acquisition spend = assumptions.marketingBudgetMo
 * - `custom`: acquisition spend = paid.budgetMo (cash still uses marketingBudgetMo)
 * Organic growth is never driven by marketing budget.
 */

export const PAID_BUDGET_MODE_LINK = 'link';
export const PAID_BUDGET_MODE_CUSTOM = 'custom';

/**
 * Effective paid acquisition $ for signups (budget ÷ CAC).
 * Does not check enabled — callers gate on paid.enabled.
 * @param {{ marketingBudgetMo?: number }} assumptions
 * @param {{ budgetMode?: string, budgetMo?: number }} [paid]
 */
export function resolvePaidBudgetMo(assumptions, paid) {
  if (paid?.budgetMode === PAID_BUDGET_MODE_CUSTOM) {
    return Math.max(0, paid.budgetMo || 0);
  }
  return Math.max(0, assumptions?.marketingBudgetMo || 0);
}

/** @param {number} budgetMo @param {number} cac */
export function estimatePaidSignups(budgetMo, cac) {
  const budget = Math.max(0, Number(budgetMo) || 0);
  const cost = Math.max(1, Number(cac) || 1);
  if (!(budget > 0)) return 0;
  return budget / cost;
}

/**
 * @param {object} channels
 * @param {number} monthIndex
 * @param {number} priorOrganic
 * @param {number} priorLogos
 * @param {Array} [referralPipeline]
 * @param {{ organicGrowthRate?: number|null, paidBudgetMo?: number|null }} [opts]
 */
export function computeChannelSignups(
  channels,
  monthIndex,
  priorOrganic,
  priorLogos,
  referralPipeline = [],
  opts = {}
) {
  if (!channels?.enabled) {
    return { organic: 0, paid: 0, referral: 0, total: 0, organicOut: priorOrganic };
  }

  let organic;
  if (channels.organic?.mode === 'growth') {
    if (monthIndex === 0) {
      organic = Math.max(0, Number(channels.organic.monthlySignups) || 0);
    } else {
      const rate =
        opts.organicGrowthRate != null
          ? opts.organicGrowthRate
          : (channels.organic.growthPctMo || 0) / 100;
      organic = Math.max(0, Number(priorOrganic) || 0) * Math.max(-1, 1 + Number(rate || 0));
    }
  } else {
    organic = Math.max(0, Number(channels.organic?.monthlySignups) || 0);
  }

  const paidBudget =
    opts.paidBudgetMo != null ? opts.paidBudgetMo : channels.paid?.budgetMo || 0;
  const paid =
    channels.paid?.enabled && paidBudget > 0
      ? estimatePaidSignups(paidBudget, channels.paid.cac)
      : 0;

  const lag = Math.max(0, channels.referral?.lagMonths ?? 1);
  let referral = 0;
  if (channels.referral?.enabled && priorLogos > 0) {
    const sourceMonth = monthIndex - lag;
    if (sourceMonth >= 0) {
      referral = Math.max(0, Number(priorLogos) || 0) * Math.max(
        0,
        Number(channels.referral.kFactor) || 0
      );
    }
  }

  const total = Math.max(0, organic + paid + referral);

  return {
    organic: roundSignups(organic),
    paid: roundSignups(paid),
    referral: roundSignups(referral),
    total: roundSignups(total),
    organicOut: roundSignups(organic),
  };
}

export function applyTamCap(currentLogos, newLogos, tamCap) {
  const candidate = Math.max(0, Number(newLogos) || 0);
  if (tamCap == null || !Number.isFinite(Number(tamCap))) return candidate;
  const headroom = Math.max(0, Number(tamCap) - Math.max(0, Number(currentLogos) || 0));
  return Math.min(candidate, headroom);
}

function roundSignups(n) {
  return Math.round(n * 10) / 10;
}

export const DEFAULT_CHANNELS = {
  enabled: true,
  // Organic: SEO + organic FB/IG posts (no Meta Ads — Ad account banned).
  organic: { mode: 'growth', monthlySignups: 10, growthPctMo: 5 },
  // Paid: Google / Reddit / LinkedIn / other — not Meta Ads.
  paid: {
    enabled: true,
    budgetMode: PAID_BUDGET_MODE_LINK,
    budgetMo: 120,
    // $/signup into the shared funnel (not $/paid-logo). The shared funnel's
    // signup → activate → paid stages are the canonical conversion controls.
    cac: 200,
  },
  referral: { enabled: false, kFactor: 0.05, lagMonths: 1 },
  tamCap: null,
};
